import { useCallback, useEffect, useState } from 'react';

import { Tab } from '@headlessui/react';

import adminAPI from '@/api/admin';
import Button, { BUTTON_KIND } from '@/components/Button';
import InfoPanelFooter from '@/components/InfoPanelFooter';
import InputCheckbox from '@/components/InputCheckbox';
import LoadingAnimation from '@/components/LoadingAnimation';
import Modal from '@/components/Modals/Modal';
import Pagination from '@/components/Pagination';
import ProfileListAdminItem from '@/components/ProfileListAdminItem';
import Search from '@/components/Search';
import Sort from '@/components/Sort';
import MESSAGES from '@/constants/messages-en';
import { classNames } from '@/helpers/strings';
import IProfile from '@/types/IProfile';

import OrgSearchDropdown from './OrgSearchDropdown';
import TableHeader from './TableHeader';
import { ProfileTabConfig } from './tabConfigs';

interface ProfilesBaseTabProps {
  config: ProfileTabConfig;
  setSuccess: React.Dispatch<React.SetStateAction<string>>;
  setError: React.Dispatch<React.SetStateAction<boolean>>;
  refreshCounts: () => Promise<void>;
  openMoveModal: (profileIDs: number[]) => void;
}

export default function ProfilesBaseTab({
  config,
  setSuccess,
  setError,
  refreshCounts,
  openMoveModal,
}: ProfilesBaseTabProps) {
  const [data, setData] = useState<IProfile[]>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sort, setSort] = useState('date');
  const [order, setOrder] = useState('desc');
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [checkedItems, setCheckedItems] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [orgSearch, setOrgSearch] = useState('');
  const [orgID, setOrgID] = useState(-1);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const listProfiles = useCallback(
    async (
      newPage: number = page,
      newPageSize: number = pageSize,
      newSort: string = sort,
      newOrder: string = order,
      newSearch: string = search,
    ) => {
      const response = await adminAPI.listProfiles({
        page: newPage,
        pageSize: newPageSize,
        sort: newSort,
        order: newOrder,
        search: newSearch,
        type: config.apiType,
        status: config.apiStatus,
        orgID,
      });

      setData(response.data?.data);
      setPage(newPage);
      setPageSize(newPageSize);
      setSort(newSort);
      setOrder(newOrder);

      return response.data;
    },
    [
      page,
      pageSize,
      sort,
      order,
      search,
      config.apiType,
      config.apiStatus,
      orgID,
    ],
  );

  const handleCheckItem = useCallback((profileID: number) => {
    setCheckedItems(prev => {
      if (prev.includes(profileID)) {
        return prev.filter(e => e !== profileID);
      }
      return [...prev, profileID];
    });
  }, []);

  const handleSelectAll = useCallback(
    (value: boolean) => {
      setSelectAll(value);
      if (value) {
        const arr = data?.map(({ id }) => id) || [];
        setCheckedItems(arr);
      } else {
        setCheckedItems([]);
      }
    },
    [data],
  );

  const handleDelete = (profileID: number) => {
    setIsLoading(true);
    setSuccess('');
    setError(false);
    adminAPI
      .deleteProfile(profileID)
      .then(() => setSuccess(MESSAGES.profile.delete.success(profileID)))
      .catch(() => setError(true))
      .then(() => setIsLoading(false))
      .then(() => refreshCounts())
      .then(() => listProfiles(1, pageSize))
      .finally(() => {});
  };

  const handleReactivate = (profileID: number) => {
    setIsLoading(true);
    setSuccess('');
    setError(false);
    adminAPI
      .reactivateProfile(profileID)
      .then(() => setSuccess(MESSAGES.profile.reactivate.success(profileID)))
      .catch(() => setError(true))
      .then(() => setIsLoading(false))
      .then(() => refreshCounts())
      .then(() => listProfiles(1, pageSize))
      .finally(() => {});
  };

  const exportCSV = useCallback(
    async (arr?: number[]) => {
      if (arr || checkedItems) {
        const response = await adminAPI.exportProfilesCSV(arr || checkedItems);
        const blob = new Blob([response.data]);
        const a = document.createElement('a');
        a.href = window.URL.createObjectURL(blob);
        a.download = 'admin_profiles.csv';
        a.click();
      }
    },
    [checkedItems],
  );

  const handleExport = (profileID: number) => {
    setIsLoading(true);
    setSuccess('');
    setError(false);
    exportCSV([profileID])
      .then(() => setSuccess(MESSAGES.profile.export.success))
      .catch(() => setError(true))
      .then(() => setIsLoading(false))
      .then(() => listProfiles(1, pageSize))
      .finally(() => {});
  };

  const handleMove = (profileID: number) => {
    const nextSelection = checkedItems.includes(profileID)
      ? checkedItems
      : [...checkedItems, profileID];
    setCheckedItems(nextSelection);
    openMoveModal(nextSelection);
  };

  const handleBulkDelete = () => {
    if (!checkedItems.length) return;
    setIsLoading(true);
    setSuccess('');
    setError(false);
    adminAPI
      .deleteProfilesShared({
        ids: checkedItems,
      })
      .then(() => setSuccess(MESSAGES.profile.delete.successMultiple))
      .catch(() => setError(true))
      .then(() => setIsLoading(false))
      .then(() => setCheckedItems([]))
      .then(() => refreshCounts())
      .then(() => listProfiles(1, pageSize))
      .finally(() => setIsDeleteOpen(false));
  };

  const handleBulkReactivate = () => {
    if (!checkedItems.length) return;
    setIsLoading(true);
    setSuccess('');
    setError(false);
    adminAPI
      .updateProfilesShared({
        ids: checkedItems,
        profile: {
          status: 'active',
        },
      })
      .then(() => setSuccess(MESSAGES.profile.reactivate.successMultiple))
      .catch(() => setError(true))
      .then(() => setIsLoading(false))
      .then(() => setCheckedItems([]))
      .then(() => refreshCounts())
      .then(() => listProfiles(1, pageSize))
      .finally(() => setIsDeleteOpen(false));
  };

  const handleBulkExport = () => {
    if (!checkedItems.length) return;
    setIsLoading(true);
    setSuccess('');
    setError(false);
    exportCSV()
      .then(() => setSuccess(MESSAGES.profile.export.success))
      .catch(() => setError(true))
      .then(() => setIsLoading(false))
      .then(() => listProfiles(1, pageSize))
      .finally(() => {
        setCheckedItems([]);
        setSelectAll(false);
      });
  };

  useEffect(() => {
    setCheckedItems([]);
    setSelectAll(false);
  }, [data]);

  return (
    <Tab.Panel unmount className="outline-none">
      <div className="py-8">
        <div className="pb-8 flex flex-col space-y-3 items-start sm:flex-row sm:space-y-0 sm:items-center sm:justify-end">
          <div className="flex flex-shrink-0 space-x-3">
            <OrgSearchDropdown
              search={orgSearch}
              setSearch={setOrgSearch}
              onSelect={(selectedOrgID, displayText) => {
                setOrgID(selectedOrgID);
                setOrgSearch(displayText);
              }}
            />
            <Search
              id={`ProfileList-${config.value}-${page}-${pageSize}-${sort}-${order}-${search}-${orgID}`}
              placeholder="Search for profiles"
              search={search}
              setSearch={setSearch}
              fetchQuery={searchQuery =>
                listProfiles(page, pageSize, sort, order, searchQuery)
              }
            />
            <Sort
              id={`ProfileList-${config.value}-${page}-${pageSize}-${sort}-${order}-${search}-${orgID}`}
              options={[
                {
                  sort: 'date',
                  order: 'desc',
                  label: 'Date created (Newest first)',
                },
                {
                  sort: 'date',
                  order: 'asc',
                  label: 'Date created (Oldest first)',
                },
                {
                  sort: 'first_name',
                  order: 'asc',
                  label: 'Name (A-Z)',
                },
                {
                  sort: 'first_name',
                  order: 'desc',
                  label: 'Name (Z-A)',
                },
              ]}
              sort={sort}
              setSort={setSort}
              order={order}
              setOrder={setOrder}
              fetchQuery={(sortQuery, orderQuery) =>
                listProfiles(page, pageSize, sortQuery, orderQuery, search)
              }
            />
          </div>
        </div>
        {data && data.length > 0 ? (
          <div className="flex flex-col">
            <div className="relative -mx-4 sm:-mx-6 md:mx-0 lg:-mx-8">
              <div className="block w-full lg:px-8">
                <div className="relative shadow-sm border border-gray-200 sm:rounded-md min-h-8">
                  <div className="lg:hidden flex items-center px-4 py-2 sm:px-6 bg-white border-b border-gray-200">
                    <InputCheckbox
                      id="select-all"
                      label=""
                      value={selectAll}
                      onChange={handleSelectAll}
                    />
                  </div>
                  <ul
                    className={classNames(
                      'divide-y divide-gray-200',
                      isLoading ? 'opacity-40' : '',
                    )}
                  >
                    <TableHeader
                      config={config}
                      selectAll={selectAll}
                      onSelectAll={handleSelectAll}
                    />
                    {data?.map((item: IProfile) => (
                      <ProfileListAdminItem
                        key={item.id}
                        id={item.id}
                        firstName={item.first_name || ''}
                        lastName={item.last_name || ''}
                        orgID={item.organisation_id}
                        orgName={item.organisation_name}
                        cardActivation={item.linked_card_activation}
                        cardID={item.linked_card_id}
                        type={config.value}
                        status={item.status || ''}
                        selected={checkedItems.includes(item.id)}
                        checkItem={() => handleCheckItem(item.id)}
                        handleDelete={() => handleDelete(item.id)}
                        handleReactivate={() => handleReactivate(item.id)}
                        handleExport={() => handleExport(item.id)}
                        handleMove={() => handleMove(item.id)}
                        profilePageURL={item.profile_page_url}
                        viewProfile={`/edit-profile/${item.id}`}
                      />
                    ))}
                  </ul>
                  {isLoading && (
                    <div className="absolute text-gray-500 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                      <LoadingAnimation className="h-16 w-16 text-brand-500 mx-auto" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="py-32">
            <h3 className="w-full text-center text-2xl leading-8 text-gray-900 font-medium">
              {MESSAGES.profile.list.empty.heading}
            </h3>
            <p className="w-full text-center mt-2 text-sm leading-5 text-gray-500">
              {MESSAGES.profile.list.empty.description}
            </p>
          </div>
        )}
      </div>

      <Pagination
        id={`ProfileList-${config.value}-${page}-${pageSize}-${sort}-${order}-${search}-${orgID}`}
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        fetchQuery={(pageQuery, pageSizeQuery) =>
          listProfiles(pageQuery, pageSizeQuery, sort, order, search)
        }
        setIsLoading={setIsLoading}
      />

      <div className="block" aria-hidden="true">
        <div className="py-8" />
      </div>

      {config.footerActions.delete && (
        <Modal
          isOpen={isDeleteOpen}
          setIsOpen={setIsDeleteOpen}
          dialogTitle={MESSAGES.profile.delete.headingMultiple}
          dialogDescription={MESSAGES.profile.delete.descriptionMultiple}
          successButtonText="Delete profiles"
          successButtonKind={BUTTON_KIND.RED}
          onSuccess={handleBulkDelete}
          isLoading={isLoading}
        />
      )}

      {config.footerActions.restore && (
        <Modal
          isOpen={isDeleteOpen}
          setIsOpen={setIsDeleteOpen}
          dialogTitle={MESSAGES.profile.reactivate.headingMultiple}
          dialogDescription={MESSAGES.profile.reactivate.descriptionMultiple}
          successButtonText="Reactivate profiles"
          successButtonKind={BUTTON_KIND.PRIMARY}
          isDisabled={!checkedItems.length}
          onSuccess={handleBulkReactivate}
        />
      )}

      <InfoPanelFooter
        className={checkedItems?.length > 1 ? '' : '!-bottom-28'}
      >
        <p className="text-sm leading-5 text-gray-500 mb-1 sm:hidden">{`${checkedItems.length} selected`}</p>
        <div className="flex items-center flex-nowrap justify-end space-x-4">
          <p className="text-sm leading-5 text-gray-500 hidden sm:block">{`${checkedItems.length} selected`}</p>
          {config.footerActions.move && (
            <Button
              kind={BUTTON_KIND.WHITE}
              buttonText="Assign profiles to another organisation"
              disabled={!checkedItems.length}
              onClick={() => openMoveModal(checkedItems)}
            />
          )}
          {config.footerActions.export && (
            <Button
              kind={BUTTON_KIND.WHITE}
              buttonText="Export selected profiles"
              disabled={!checkedItems.length}
              onClick={handleBulkExport}
            />
          )}
          {config.footerActions.delete && (
            <Button
              kind={BUTTON_KIND.RED}
              buttonText="Delete profiles"
              disabled={!checkedItems.length}
              onClick={() => setIsDeleteOpen(true)}
            />
          )}
          {config.footerActions.restore && (
            <Button
              kind={BUTTON_KIND.WHITE}
              buttonText="Reactivate profiles"
              disabled={!checkedItems.length}
              onClick={() => setIsDeleteOpen(true)}
            />
          )}
        </div>
      </InfoPanelFooter>
    </Tab.Panel>
  );
}
